import "./App.css";

import { useEffect, useState } from "react";

import { Link } from "react-router-dom";
import { WEB_APP_URL } from "./config";

function Home() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [hasLaunched, setHasLaunched] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const targetDate = new Date("2026-01-01T00:00:00").getTime();
      const difference = targetDate - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor(
          (difference % (1000 * 60 * 60)) / (1000 * 60)
        );
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });
        setHasLaunched(false);
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setHasLaunched(true);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const form = e.target;
    const formData = new FormData(form);

    try {
      const response = await fetch(form.action, {
        method: form.method,
        body: formData,
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        setSubmitStatus("success");
        form.reset();
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-black text-gray-200">
      {/* Navbar */}
      <header className="bg-black bg-opacity-70 backdrop-blur-lg fixed top-0 w-full z-50 shadow-lg">
        <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <img
              src="/images/logo.png"
              alt="Fit-Match Logo"
              className="h-16 w-auto"
            />
            <Link to="/" className="text-2xl font-bold gradient-text">
              FIT-MATCH
            </Link>
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <a href="#how-it-works" className="nav-link font-medium">
              How It Works
            </a>
            <Link to="/our-story" className="nav-link font-medium">
              Our Story
            </Link>
            <Link to="/professionals" className="nav-link font-medium">
              Pros
            </Link>
            <Link to="/training-providers" className="nav-link font-medium">
              Providers
            </Link>
            <Link to="/communities" className="nav-link font-medium">
              Communities
            </Link>
            <Link to="/facilities" className="nav-link font-medium">
              Facilities
            </Link>
            <a
              href={WEB_APP_URL}
              className="nav-link font-medium px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white"
            >
              Login
            </a>
          </div>
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="text-white focus:outline-none"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16m-4 6h4"
                ></path>
              </svg>
            </button>
          </div>
        </nav>
        {/* Mobile Menu */}
        <div
          className={`${
            isMobileMenuOpen ? "block" : "hidden"
          } md:hidden bg-black bg-opacity-90 py-4`}
        >
          <a
            href="#how-it-works"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            How It Works
          </a>
          <Link
            to="/our-story"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Our Story
          </Link>
          <Link
            to="/professionals"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Pros
          </Link>
          <Link
            to="/training-providers"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Providers
          </Link>
          <Link
            to="/communities"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Communities
          </Link>
          <Link
            to="/facilities"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Facilities
          </Link>
          <a
            href={WEB_APP_URL}
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Login
          </a>
        </div>
      </header>

      <main className="pt-24">
        {/* Hero Section */}
        <section className="hero-bg py-20 md:py-32 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-70"></div>
          <div className="container mx-auto px-6 relative z-10">
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight tracking-tight">
              Your Fitness. Your <span className="gradient-text">Match.</span>
            </h1>
            <p className="mt-4 text-lg md:text-xl max-w-2xl mx-auto">
              Connecting you to the perfect fitness professional, no booking
              fees, just results.
            </p>

            {/* Countdown Timer or Download Button */}
            {hasLaunched ? null : (
              <div className="mt-8 mb-8">
                <h3 className="text-xl md:text-2xl font-semibold mb-4 text-white">
                  Launch Countdown
                </h3>
                <div className="flex justify-center items-center space-x-4 md:space-x-8">
                  <div className="bg-gray-800 bg-opacity-80 rounded-lg p-4 md:p-6 text-center min-w-[80px] md:min-w-[100px]">
                    <div className="text-2xl md:text-4xl font-bold text-blue-fit-match">
                      {timeLeft.days}
                    </div>
                    <div className="text-sm md:text-base text-gray-300">
                      Days
                    </div>
                  </div>
                  <div className="bg-gray-800 bg-opacity-80 rounded-lg p-4 md:p-6 text-center min-w-[80px] md:min-w-[100px]">
                    <div className="text-2xl md:text-4xl font-bold text-blue-fit-match">
                      {timeLeft.hours}
                    </div>
                    <div className="text-sm md:text-base text-gray-300">
                      Hours
                    </div>
                  </div>
                  <div className="bg-gray-800 bg-opacity-80 rounded-lg p-4 md:p-6 text-center min-w-[80px] md:min-w-[100px]">
                    <div className="text-2xl md:text-4xl font-bold text-blue-fit-match">
                      {timeLeft.minutes}
                    </div>
                    <div className="text-sm md:text-base text-gray-300">
                      Minutes
                    </div>
                  </div>
                  <div className="bg-gray-800 bg-opacity-80 rounded-lg p-4 md:p-6 text-center min-w-[80px] md:min-w-[100px]">
                    <div className="text-2xl md:text-4xl font-bold text-blue-fit-match">
                      {timeLeft.seconds}
                    </div>
                    <div className="text-sm md:text-base text-gray-300">
                      Seconds
                    </div>
                  </div>
                </div>
                <p className="text-sm md:text-base text-gray-300 mt-4">
                  Until January 1st, 2026
                </p>
              </div>
            )}

            <div className="mt-8 flex justify-center space-x-4">
              <Link
                to="/clients"
                className="bg-blue-fit-match text-white font-bold py-3 px-8 rounded-full shadow-lg hover:bg-blue-600 transition duration-300"
              >
                Clients
              </Link>
              <Link
                to="/professionals"
                className="bg-transparent border border-blue-fit-match text-blue-fit-match font-bold py-3 px-8 rounded-full hover:bg-blue-fit-match hover:text-white transition duration-300"
              >
                Professionals
              </Link>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-20 bg-gray-950">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              The new way to connect.
            </h2>
            <p className="text-lg max-w-3xl mx-auto text-gray-400">
              Fit-Match is your premier platform for discovering and connecting
              with fitness professionals near you. We're breaking down the
              barriers to fitness by providing a direct, fee-free connection
              between clients and pros.
            </p>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gray-800 p-8 rounded-xl shadow-xl transform hover:scale-105 transition duration-300">
                <div className="text-blue-fit-match text-4xl mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 mx-auto"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2">Find Local Pros</h3>
                <p className="text-gray-400">
                  Use our intuitive map view to find trainers, physios, and boot
                  camps right in your neighborhood.
                </p>
              </div>
              <div className="bg-gray-800 p-8 rounded-xl shadow-xl transform hover:scale-105 transition duration-300">
                <div className="text-blue-fit-match text-4xl mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 mx-auto"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 7V3m8 4V3m-9 8h.01M3 21h18a2 2 0 002-2V8.656a2 2 0 00-.606-1.424l-3.342-3.342A2 2 0 0013.586 3H10.414A2 2 0 009 5.828v2.172a2 2 0 00-.606 1.424l-3.342 3.342A2 2 0 003 16.344V21z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2">Zero Booking Fees</h3>
                <p className="text-gray-400">
                  Clients pay absolutely nothing to use our platform. Connect
                  directly and book sessions without hidden costs.
                </p>
              </div>
              <div className="bg-gray-800 p-8 rounded-xl shadow-xl transform hover:scale-105 transition duration-300">
                <div className="text-blue-fit-match text-4xl mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 mx-auto"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 8c-3.313 0-6 2.687-6 6s2.687 6 6 6 6-2.687 6-6-2.687-6-6-6z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 12c0-4.97-4.03-9-9-9s-9 4.03-9 9 4.03 9 9 9z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2">Flexible Memberships</h3>
                <p className="text-gray-400">
                  Fitness professionals pay a low, monthly fee, giving them
                  unlimited access to a community of potential clients.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How it Works Section */}
        <section id="how-it-works" className="py-20 bg-black">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              How It Works
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1">
                <div className="space-y-8">
                  <div>
                    <h3 className="text-2xl font-bold flex items-center mb-2">
                      <span className="text-blue-fit-match mr-4">01</span>{" "}
                      Discover
                    </h3>
                    <p className="text-gray-400">
                      Browse our comprehensive map to find fitness professionals
                      based on your location and specific needs, from personal
                      training to physiotherapy.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold flex items-center mb-2">
                      <span className="text-blue-fit-match mr-4">02</span>{" "}
                      Connect
                    </h3>
                    <p className="text-gray-400">
                      Found your match? Send a direct message to connect, ask
                      questions, and discuss your fitness goals. Our chat
                      feature makes it simple and secure.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold flex items-center mb-2">
                      <span className="text-blue-fit-match mr-4">03</span> Train
                    </h3>
                    <p className="text-gray-400">
                      Once you've aligned on details, book your sessions
                      directly with the professional. Enjoy your journey to a
                      healthier you, without any extra charges from us.
                    </p>
                  </div>
                </div>
              </div>
              <div className="order-1 md:order-2 flex justify-center">
                <div className="app-screenshot-frame rotate-6 transform transition duration-300 hover:rotate-0">
                  <div
                    className="app-screenshot-inner"
                    style={{
                      backgroundImage:
                        "url('/images/screenshots/map-view.PNG')",
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Screenshots Section */}
        <section id="screenshots" className="py-20 bg-gray-950">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-12">
              App Screenshots
            </h2>
            <div className="flex flex-wrap justify-center items-center gap-12">
              <div className="app-screenshot-frame">
                <div
                  className="app-screenshot-inner"
                  style={{
                    backgroundImage:
                      "url('/images/screenshots/login-screen.PNG')",
                  }}
                ></div>
              </div>
              <div className="app-screenshot-frame rotate-6 transform transition duration-300 hover:rotate-0">
                <div
                  className="app-screenshot-inner"
                  style={{
                    backgroundImage:
                      "url('/images/screenshots/professional-profile.PNG')",
                  }}
                ></div>
              </div>
              <div className="app-screenshot-frame -rotate-6 transform transition duration-300 hover:rotate-0">
                <div
                  className="app-screenshot-inner"
                  style={{
                    backgroundImage:
                      "url('/images/screenshots/messages-page.PNG')",
                  }}
                ></div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Story Section */}
        <section id="story" className="py-20 bg-black">
          <div className="container mx-auto px-6 text-center max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Story</h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Fit-Match was born from a simple observation: the fitness
              industry, while vibrant, lacked a direct, client-friendly way to
              connect with professionals. We saw a world of talent—from
              passionate personal trainers to skilled physiotherapists—who were
              often hidden behind complex booking platforms and hefty fees. We
              wanted to change that. Our mission is to empower both clients and
              professionals by creating a transparent, simple, and affordable
              platform. We believe that finding your perfect fitness match
              should be about connection, not commissions.
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20 bg-gray-950">
          <div className="container mx-auto px-6 text-center max-w-xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Get in Touch
            </h2>
            <p className="text-lg text-gray-400 mb-8">
              Have questions? We'd love to hear from you. Fill out the form
              below and we'll get back to you as soon as possible.
            </p>
            <form
              onSubmit={handleSubmit}
              action="https://formspree.io/f/mpwykybg"
              method="POST"
              className="space-y-6"
            >
              <div>
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  className="w-full bg-gray-800 border border-gray-700 rounded-md py-3 px-4 focus:outline-none focus:border-blue-fit-match transition duration-300"
                  required
                />
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  className="w-full bg-gray-800 border border-gray-700 rounded-md py-3 px-4 focus:outline-none focus:border-blue-fit-match transition duration-300"
                  required
                />
              </div>
              <div>
                <textarea
                  name="message"
                  placeholder="Your Message"
                  rows="5"
                  className="w-full bg-gray-800 border border-gray-700 rounded-md py-3 px-4 focus:outline-none focus:border-blue-fit-match transition duration-300"
                  required
                ></textarea>
              </div>

              {/* Status Messages */}
              {submitStatus === "success" && (
                <div className="bg-green-800 border border-green-600 text-green-200 px-4 py-3 rounded-md">
                  Thank you! Your message has been sent successfully. We'll get
                  back to you soon.
                </div>
              )}

              {submitStatus === "error" && (
                <div className="bg-red-800 border border-red-600 text-red-200 px-4 py-3 rounded-md">
                  Sorry, there was an error sending your message. Please try
                  again or contact us directly.
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full font-bold py-3 px-4 rounded-md shadow-lg transition duration-300 ${
                    isSubmitting
                      ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                      : "bg-blue-fit-match text-white hover:bg-blue-600"
                  }`}
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>
              </div>
            </form>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 py-8 text-center text-gray-400">
        <p>&copy; 2024 Fit-Match. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default Home;
